import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  Sparkles, ArrowRight, ArrowLeft, Star, Moon, Sun, Heart, 
  Leaf, CheckCircle, SkipForward, Calendar, MapPin, Clock,
  Target, Utensils, Brain, Compass, ChevronRight
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Zodiac sign data
const ZODIAC_SIGNS = [
  { sign: "Aries", dates: "Mar 21 - Apr 19", element: "Fire", symbol: "♈", traits: "Bold, ambitious, confident leader" },
  { sign: "Taurus", dates: "Apr 20 - May 20", element: "Earth", symbol: "♉", traits: "Patient, reliable, devoted" },
  { sign: "Gemini", dates: "May 21 - Jun 20", element: "Air", symbol: "♊", traits: "Adaptable, curious, communicative" },
  { sign: "Cancer", dates: "Jun 21 - Jul 22", element: "Water", symbol: "♋", traits: "Intuitive, sentimental, protective" },
  { sign: "Leo", dates: "Jul 23 - Aug 22", element: "Fire", symbol: "♌", traits: "Creative, passionate, generous" },
  { sign: "Virgo", dates: "Aug 23 - Sep 22", element: "Earth", symbol: "♍", traits: "Analytical, practical, loyal" },
  { sign: "Libra", dates: "Sep 23 - Oct 22", element: "Air", symbol: "♎", traits: "Diplomatic, fair, social" },
  { sign: "Scorpio", dates: "Oct 23 - Nov 21", element: "Water", symbol: "♏", traits: "Passionate, resourceful, brave" },
  { sign: "Sagittarius", dates: "Nov 22 - Dec 21", element: "Fire", symbol: "♐", traits: "Optimistic, adventurous, honest" },
  { sign: "Capricorn", dates: "Dec 22 - Jan 19", element: "Earth", symbol: "♑", traits: "Disciplined, responsible, ambitious" },
  { sign: "Aquarius", dates: "Jan 20 - Feb 18", element: "Air", symbol: "♒", traits: "Progressive, original, independent" },
  { sign: "Pisces", dates: "Feb 19 - Mar 20", element: "Water", symbol: "♓", traits: "Compassionate, artistic, intuitive" }
];

// Life path descriptions
const LIFE_PATH_MEANINGS = {
  1: { title: "The Leader", description: "You are a natural pioneer with strong independence and creativity. Your path is about self-discovery and leading others." },
  2: { title: "The Peacemaker", description: "You bring harmony and balance to relationships. Your path involves diplomacy, cooperation, and emotional sensitivity." },
  3: { title: "The Creative", description: "Expression and joy are your gifts. Your path involves creativity, communication, and inspiring others." },
  4: { title: "The Builder", description: "You create solid foundations. Your path involves hard work, organization, and building lasting structures." },
  5: { title: "The Freedom Seeker", description: "Change and adventure call to you. Your path involves adaptability, travel, and embracing life's variety." },
  6: { title: "The Nurturer", description: "Love and responsibility guide you. Your path involves family, healing, and service to others." },
  7: { title: "The Seeker", description: "Wisdom and spirituality draw you inward. Your path involves introspection, analysis, and spiritual growth." },
  8: { title: "The Powerhouse", description: "Achievement and abundance are your destiny. Your path involves business, authority, and material success." },
  9: { title: "The Humanitarian", description: "Compassion and global awareness define you. Your path involves service, wisdom, and universal love." },
  11: { title: "The Intuitive", description: "Master Number - You have heightened intuition and spiritual insight. Your path involves inspiration and illumination." },
  22: { title: "The Master Builder", description: "Master Number - You can turn dreams into reality on a large scale. Your path involves creating lasting legacies." },
  33: { title: "The Master Teacher", description: "Master Number - You uplift humanity through compassion. Your path involves selfless service and spiritual teaching." }
};

// Spirit animal associations
const SPIRIT_ANIMALS = [
  { name: "Wolf", emoji: "🐺", traits: ["loyalty", "family", "intuition", "community"], description: "Loyalty, intuition, and strong family bonds" },
  { name: "Butterfly", emoji: "🦋", traits: ["transformation", "change", "growth", "beauty"], description: "Transformation, renewal, and embracing change" },
  { name: "Eagle", emoji: "🦅", traits: ["vision", "freedom", "courage", "leadership"], description: "Vision, freedom, and spiritual awareness" },
  { name: "Bear", emoji: "🐻", traits: ["strength", "healing", "introspection", "protection"], description: "Strength, healing, and grounding energy" },
  { name: "Owl", emoji: "🦉", traits: ["wisdom", "intuition", "mystery", "spirituality"], description: "Wisdom, intuition, and seeing through deception" },
  { name: "Deer", emoji: "🦌", traits: ["gentleness", "grace", "peace", "sensitivity"], description: "Gentleness, grace, and natural beauty" },
  { name: "Fox", emoji: "🦊", traits: ["cunning", "adaptability", "quick thinking", "observation"], description: "Cleverness, adaptability, and awareness" },
  { name: "Dragonfly", emoji: "🪻", traits: ["change", "light", "emotions", "self-realization"], description: "Transformation, light, and emotional depth" },
  { name: "Hummingbird", emoji: "🐦", traits: ["joy", "lightness", "energy", "playfulness"], description: "Joy, presence, and accomplishing the impossible" },
  { name: "Turtle", emoji: "🐢", traits: ["patience", "wisdom", "longevity", "grounding"], description: "Patience, wisdom, and connection to earth" }
];

// Angel number meanings
const ANGEL_NUMBERS = {
  "111": { meaning: "New Beginnings", message: "Your thoughts are manifesting rapidly. Stay positive and focused on your desires." },
  "222": { meaning: "Balance & Trust", message: "Trust the process. Everything is working out as it should. Keep the faith." },
  "333": { meaning: "Divine Support", message: "The Ascended Masters are near, guiding and assisting you. Ask for their help." },
  "444": { meaning: "Protection", message: "Angels surround you. You are protected, supported, and on the right path." },
  "555": { meaning: "Major Change", message: "Significant changes are coming. Embrace transformation with an open heart." },
  "666": { meaning: "Balance & Reflection", message: "Refocus your thoughts from material concerns to spiritual growth and service." },
  "777": { meaning: "Spiritual Awakening", message: "You're on the right path. Miracles are happening. Keep going!" },
  "888": { meaning: "Abundance", message: "Financial and material abundance is flowing to you. Stay open to receiving." },
  "999": { meaning: "Completion", message: "A phase is ending. Prepare for new beginnings and embrace closure." },
  "1111": { meaning: "Manifestation Portal", message: "A powerful gateway. Your thoughts create reality. Focus on what you want." }
};

const OnboardingQuiz = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  
  // Quiz answers state
  const [answers, setAnswers] = useState({
    // Personal info
    gender: "",
    // Birth info
    birthDate: "",
    birthTime: "",
    birthPlace: "",
    // Wellness goals
    wellnessGoals: [],
    // Dietary
    dietaryPreference: "",
    foodAllergies: [],
    // Spirituality
    spiritualRelationship: "",
    peaceBrings: "",
    intuitionImportance: "",
    spiritualTraditions: "",
    // Animal & Numbers
    hasAnimalConnection: "",
    animalDescription: "",
    noticesAngelNumbers: "",
    frequentNumbers: "",
    // Current state
    currentFeelings: [],
    lifeAreaToImprove: ""
  });

  const totalSteps = 7;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Calculate Sun Sign from birth date
  const calculateSunSign = (dateStr) => {
    if (!dateStr) return null;
    
    // Parse date string directly to avoid timezone issues
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    
    if (isNaN(month) || isNaN(day)) return null;
    
    if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return ZODIAC_SIGNS[0];
    if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return ZODIAC_SIGNS[1];
    if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return ZODIAC_SIGNS[2];
    if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return ZODIAC_SIGNS[3];
    if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return ZODIAC_SIGNS[4];
    if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return ZODIAC_SIGNS[5];
    if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return ZODIAC_SIGNS[6];
    if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return ZODIAC_SIGNS[7];
    if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return ZODIAC_SIGNS[8];
    if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return ZODIAC_SIGNS[9];
    if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return ZODIAC_SIGNS[10];
    if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) return ZODIAC_SIGNS[11];
    return null;
  };

  // Calculate Moon Sign from birth date and time
  // Moon changes sign every ~2.5 days, this is an approximation
  const calculateMoonSign = (dateStr, timeStr) => {
    if (!dateStr) return null;
    
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    
    // Calculate days since a known new moon (Jan 6, 2000 was a new moon in Capricorn)
    const knownNewMoon = new Date(2000, 0, 6);
    const birthDate = new Date(year, month - 1, day);
    const daysDiff = Math.floor((birthDate - knownNewMoon) / (1000 * 60 * 60 * 24));
    
    // Moon cycle is approximately 29.53 days, each sign is ~2.46 days
    const lunarCycle = 29.53;
    const daysPerSign = lunarCycle / 12;
    
    // Add time offset if provided (each hour = ~0.1 sign movement)
    let hourOffset = 0;
    if (timeStr) {
      const timeParts = timeStr.split(':');
      if (timeParts.length >= 1) {
        hourOffset = parseInt(timeParts[0], 10) / 24 * daysPerSign;
      }
    }
    
    // Calculate position in lunar cycle
    const positionInCycle = ((daysDiff + hourOffset) % lunarCycle + lunarCycle) % lunarCycle;
    const signIndex = Math.floor(positionInCycle / daysPerSign) % 12;
    
    // Moon sign order starting from Capricorn (the sign of the known new moon)
    const moonSignOrder = [9, 10, 11, 0, 1, 2, 3, 4, 5, 6, 7, 8]; // Capricorn -> Sagittarius
    
    return {
      ...ZODIAC_SIGNS[moonSignOrder[signIndex]],
      type: "Moon"
    };
  };

  // Calculate Rising Sign (Ascendant) from birth time
  // Rising sign changes every ~2 hours throughout the day
  const calculateRisingSign = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    
    const timeParts = timeStr.split(':');
    if (timeParts.length < 1) return null;
    
    const hour = parseInt(timeParts[0], 10);
    const minute = timeParts.length > 1 ? parseInt(timeParts[1], 10) : 0;
    
    if (isNaN(hour)) return null;
    
    // Get the sun sign for this date (this affects rising calculation)
    const sunSign = calculateSunSign(dateStr);
    if (!sunSign) return null;
    
    // Find sun sign index
    const sunSignIndex = ZODIAC_SIGNS.findIndex(z => z.sign === sunSign.sign);
    
    // Calculate time in decimal hours
    const decimalTime = hour + (minute / 60);
    
    // Rising sign is roughly: Sun sign at sunrise (~6am), then advances 1 sign every 2 hours
    // At 6am, rising = sun sign. At 8am, rising = sun sign + 1, etc.
    const hoursFromSunrise = decimalTime - 6;
    const signOffset = Math.floor(hoursFromSunrise / 2);
    
    // Handle wraparound
    const risingIndex = ((sunSignIndex + signOffset) % 12 + 12) % 12;
    
    return {
      ...ZODIAC_SIGNS[risingIndex],
      type: "Rising"
    };
  };

  // Calculate Life Path Number - Using proper numerology method
  // Reduces year, month, and day separately (preserving master numbers), then adds them
  const calculateLifePath = (dateStr) => {
    if (!dateStr) return null;
    
    // Parse date string directly to avoid timezone issues
    // dateStr format: "YYYY-MM-DD"
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    
    // Helper to reduce a number to single digit or master number
    const reduceNumber = (num) => {
      let sum = num;
      while (sum > 9 && sum !== 11 && sum !== 22 && sum !== 33) {
        sum = sum.toString().split('').reduce((acc, digit) => acc + parseInt(digit, 10), 0);
      }
      return sum;
    };
    
    // Reduce year digits: e.g., 1988 → 1+9+8+8 = 26 → 2+6 = 8
    const yearSum = year.toString().split('').reduce((acc, digit) => acc + parseInt(digit, 10), 0);
    const reducedYear = reduceNumber(yearSum);
    
    // Month is already 1-12, reduce if needed (11 stays as master)
    const reducedMonth = reduceNumber(month);
    
    // Day can be 1-31, reduce if needed (but preserve 11, 22)
    const reducedDay = reduceNumber(day);
    
    // Add the three reduced numbers
    const total = reducedYear + reducedMonth + reducedDay;
    
    // Final reduction (preserving master numbers)
    // For 1988-05-23: 8 + 5 + 5 = 18 → 1+8 = 9
    return reduceNumber(total);
  };

  // Determine spirit animal based on traits
  const determineAnimalGuide = () => {
    if (answers.hasAnimalConnection === "yes" && answers.animalDescription) {
      const desc = answers.animalDescription.toLowerCase();
      for (const animal of SPIRIT_ANIMALS) {
        if (desc.includes(animal.name.toLowerCase())) {
          return animal;
        }
      }
    }
    
    // Determine based on personality traits from goals and feelings
    const traits = [];
    if (answers.wellnessGoals.includes("stress")) traits.push("introspection", "healing");
    if (answers.wellnessGoals.includes("spiritual")) traits.push("spirituality", "intuition");
    if (answers.wellnessGoals.includes("energy")) traits.push("energy", "playfulness");
    if (answers.wellnessGoals.includes("mindfulness")) traits.push("wisdom", "peace");
    if (answers.currentFeelings.includes("lonely")) traits.push("community", "family");
    if (answers.currentFeelings.includes("stuck")) traits.push("transformation", "change");
    
    // Find best match
    let bestMatch = SPIRIT_ANIMALS[0];
    let bestScore = 0;
    
    for (const animal of SPIRIT_ANIMALS) {
      const score = animal.traits.filter(t => traits.some(ut => t.includes(ut) || ut.includes(t))).length;
      if (score > bestScore) {
        bestScore = score;
        bestMatch = animal;
      }
    }
    
    return bestMatch;
  };

  // Determine angel number
  const determineAngelNumber = () => {
    if (answers.noticesAngelNumbers === "yes" && answers.frequentNumbers) {
      const numbers = answers.frequentNumbers.match(/\d+/g);
      if (numbers) {
        for (const num of numbers) {
          if (ANGEL_NUMBERS[num]) return { number: num, ...ANGEL_NUMBERS[num] };
        }
      }
    }
    
    // Assign based on life path or goals
    const lifePath = calculateLifePath(answers.birthDate);
    if (lifePath) {
      const numberMap = { 1: "111", 2: "222", 3: "333", 4: "444", 5: "555", 6: "666", 7: "777", 8: "888", 9: "999", 11: "1111", 22: "222", 33: "333" };
      const angelNum = numberMap[lifePath] || "444";
      return { number: angelNum, ...ANGEL_NUMBERS[angelNum] };
    }
    
    return { number: "444", ...ANGEL_NUMBERS["444"] };
  };

  // Generate book recommendations - Life Path readable is ALWAYS first
  const generateBookRecommendations = () => {
    const recommendations = [];
    
    // FIRST: Always recommend the user's Life Path readable
    const lifePath = calculateLifePath(answers.birthDate);
    if (lifePath) {
      const lifePathTitles = {
        1: { id: "life-path-1", title: "Life Path 1: The Leader", reason: "Your personal numerology guide" },
        2: { id: "life-path-2", title: "Life Path 2: The Peacemaker", reason: "Your personal numerology guide" },
        3: { id: "life-path-3", title: "Life Path 3: The Creative", reason: "Your personal numerology guide" },
        4: { id: "life-path-4", title: "Life Path 4: The Builder", reason: "Your personal numerology guide" },
        5: { id: "life-path-5", title: "Life Path 5: The Freedom Seeker", reason: "Your personal numerology guide" },
        6: { id: "life-path-6", title: "Life Path 6: The Nurturer", reason: "Your personal numerology guide" },
        7: { id: "life-path-7", title: "Life Path 7: The Seeker", reason: "Your personal numerology guide" },
        8: { id: "life-path-8", title: "Life Path 8: The Powerhouse", reason: "Your personal numerology guide" },
        9: { id: "life-path-9", title: "Life Path 9: The Humanitarian", reason: "Your personal numerology guide" },
        11: { id: "life-path-11", title: "Life Path 11: The Intuitive", reason: "Your master number guide" },
        22: { id: "life-path-22", title: "Life Path 22: The Master Builder", reason: "Your master number guide" },
        33: { id: "life-path-33", title: "Life Path 33: The Master Teacher", reason: "Your master number guide" }
      };
      
      if (lifePathTitles[lifePath]) {
        recommendations.push(lifePathTitles[lifePath]);
      }
    }
    
    // Based on wellness goals
    if (answers.wellnessGoals.includes("stress")) {
      recommendations.push({ id: "finding-calm", title: "Finding Calm in the Chaos", reason: "Perfect for managing stress and anxiety" });
    }
    if (answers.wellnessGoals.includes("spiritual")) {
      recommendations.push({ id: "awakening-101", title: "The Awakening Path", reason: "Deepen your spiritual connection" });
    }
    if (answers.wellnessGoals.includes("sleep")) {
      recommendations.push({ id: "restful-nights", title: "Restful Nights", reason: "Improve your sleep quality naturally" });
    }
    if (answers.wellnessGoals.includes("mindfulness")) {
      recommendations.push({ id: "present-moment", title: "The Present Moment", reason: "Cultivate mindfulness practice" });
    }
    if (answers.wellnessGoals.includes("nutrition")) {
      recommendations.push({ id: "nourish-soul", title: "Nourish Your Soul", reason: "Learn about holistic nutrition" });
    }
    
    // Based on current feelings
    if (answers.currentFeelings.includes("stuck")) {
      recommendations.push({ id: "breakthrough", title: "Breaking Through Barriers", reason: "Overcome feeling stuck" });
    }
    if (answers.currentFeelings.includes("lonely")) {
      recommendations.push({ id: "connection", title: "The Art of Connection", reason: "Build meaningful relationships" });
    }
    
    // Based on life area
    if (answers.lifeAreaToImprove === "career") {
      recommendations.push({ id: "purpose-work", title: "Purpose-Driven Work", reason: "Align your career with your soul" });
    }
    if (answers.lifeAreaToImprove === "relationships") {
      recommendations.push({ id: "love-languages", title: "Love Languages Decoded", reason: "Transform your relationships" });
    }
    if (answers.lifeAreaToImprove === "personal") {
      recommendations.push({ id: "inner-growth", title: "The Inner Growth Journey", reason: "Personal transformation guide" });
    }
    
    // Add default if not enough
    const defaults = [
      { id: "awakening-101", title: "The Awakening Path", reason: "Begin your wellness journey" },
      { id: "self-love", title: "Radical Self-Love", reason: "Foundation of all healing" },
      { id: "manifestation", title: "Manifest Your Dreams", reason: "Create your ideal life" },
      { id: "healing-trauma", title: "Healing Generational Patterns", reason: "Break free from the past" },
      { id: "daily-rituals", title: "Sacred Daily Rituals", reason: "Transform your daily routine" }
    ];
    
    while (recommendations.length < 5) {
      const next = defaults.find(d => !recommendations.some(r => r.id === d.id));
      if (next) recommendations.push(next);
      else break;
    }
    
    return recommendations.slice(0, 5);
  };

  // Process and submit quiz
  const processQuiz = async () => {
    const sunSign = calculateSunSign(answers.birthDate);
    const lifePath = calculateLifePath(answers.birthDate);
    const moonSign = calculateMoonSign(answers.birthDate, answers.birthTime);
    const risingSign = calculateRisingSign(answers.birthDate, answers.birthTime);
    const animalGuide = determineAnimalGuide();
    const angelNumber = determineAngelNumber();
    const bookRecommendations = generateBookRecommendations();
    
    // Debug logging
    if (answers.birthDate) {
      const parts = answers.birthDate.split('-');
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      console.log(`[Quiz Debug] Birth Date: ${answers.birthDate}, Time: ${answers.birthTime}`);
      console.log(`[Quiz Debug] Parsed: Year=${year}, Month=${month}, Day=${day}`);
      console.log(`[Quiz Debug] Sun Sign: ${sunSign?.sign}, Moon Sign: ${moonSign?.sign}, Rising: ${risingSign?.sign}`);
      console.log(`[Quiz Debug] Life Path: ${lifePath}`);
    }
    
    const results = {
      // Astrological
      sunSign: sunSign ? {
        sign: sunSign.sign,
        symbol: sunSign.symbol,
        element: sunSign.element,
        traits: sunSign.traits,
        dates: sunSign.dates
      } : null,
      moonSign: moonSign ? {
        sign: moonSign.sign,
        symbol: moonSign.symbol,
        element: moonSign.element,
        traits: moonSign.traits
      } : null,
      risingSign: risingSign ? {
        sign: risingSign.sign,
        symbol: risingSign.symbol,
        element: risingSign.element,
        traits: risingSign.traits
      } : null,
      lifePath: lifePath ? {
        number: lifePath,
        ...LIFE_PATH_MEANINGS[lifePath]
      } : null,
      
      // Spiritual
      animalGuide: {
        name: animalGuide.name,
        emoji: animalGuide.emoji,
        description: animalGuide.description
      },
      angelNumber: angelNumber,
      spiritualityStatus: answers.spiritualRelationship || "exploring",
      
      // Personal Info
      gender: answers.gender,
      
      // Preferences
      wellnessGoals: answers.wellnessGoals,
      dietaryPreference: answers.dietaryPreference,
      foodAllergies: answers.foodAllergies,
      
      // Recommendations
      bookRecommendations: bookRecommendations,
      
      // Raw data for future use
      birthInfo: {
        date: answers.birthDate,
        time: answers.birthTime,
        place: answers.birthPlace
      },
      currentState: {
        feelings: answers.currentFeelings,
        focusArea: answers.lifeAreaToImprove
      }
    };
    
    return results;
  };

  const handleSubmitQuiz = async () => {
    setIsSubmitting(true);
    try {
      const results = await processQuiz();
      
      // Save to backend - backend calculates accurate zodiac data
      const response = await axios.post(`${API}/api/quiz/save`, {
        answers: answers,
        results: results
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Use ACCURATE results from backend (server-side calculations are more reliable)
      const accurateData = response.data.accurate_results;
      
      // Override frontend calculations with accurate backend data
      const finalResults = {
        ...results,
        // Use backend's accurate calculations for Big Three
        sunSign: accurateData?.sunSign ? {
          ...results.sunSign,
          sign: accurateData.sunSign.sign,
          symbol: accurateData.sunSign.symbol,
          element: accurateData.sunSign.element,
          traits: accurateData.sunSign.traits
        } : results.sunSign,
        moonSign: accurateData?.moonSign ? {
          sign: accurateData.moonSign.sign,
          symbol: accurateData.moonSign.symbol,
          element: accurateData.moonSign.element,
          traits: accurateData.moonSign.traits
        } : results.moonSign,
        risingSign: accurateData?.risingSign ? {
          sign: accurateData.risingSign.sign,
          symbol: accurateData.risingSign.symbol,
          element: accurateData.risingSign.element,
          traits: accurateData.risingSign.traits
        } : results.risingSign,
        lifePath: accurateData?.lifePath || results.lifePath
      };
      
      console.log("[Quiz] Using accurate server-calculated zodiac data:", accurateData);
      
      // Refresh user data
      await refreshUser();
      
      setQuizResults(finalResults);
      setShowResults(true);
      toast.success("Your wellness profile has been created!");
    } catch (error) {
      console.error("Failed to save quiz:", error);
      toast.error("Failed to save quiz results. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    try {
      await axios.post(`${API}/api/quiz/skip`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate("/dashboard");
    } catch (error) {
      navigate("/dashboard");
    }
  };

  const handleMultiSelect = (field, value, max = null) => {
    setAnswers(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      }
      if (max && current.length >= max) {
        return prev;
      }
      return { ...prev, [field]: [...current, value] };
    });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return answers.gender;
      case 1: return answers.birthDate;
      case 2: return answers.wellnessGoals.length > 0;
      case 3: return answers.dietaryPreference;
      case 4: return answers.spiritualRelationship;
      case 5: return answers.hasAnimalConnection && answers.noticesAngelNumbers;
      case 6: return answers.currentFeelings.length > 0 && !!answers.lifeAreaToImprove;
      default: return true;
    }
  };

  // Generate journey recommendations based on quiz answers
  const generateJourneyRecommendations = () => {
    const recommendations = [];
    const goals = answers.wellnessGoals || [];
    const feelings = answers.currentFeelings || [];
    const focusArea = answers.lifeAreaToImprove;
    const spirituality = answers.spiritualRelationship;
    
    // FIRST PRIORITY: Exploratory features for new users to discover the app
    // Morning Routine is always a great starting point
    recommendations.push({
      name: "Morning Routine",
      path: "/morning",
      icon: "🌅",
      gradient: "bg-gradient-to-br from-amber-500 to-orange-600",
      reason: "Start each day with intention and alignment"
    });
    
    // Daily Vibe Check helps users understand the app's wellness tracking
    recommendations.push({
      name: "Daily Vibe Check",
      path: "/vibe-check",
      icon: "💫",
      gradient: "bg-gradient-to-br from-pink-500 to-rose-600",
      reason: "Track your emotional wellness journey"
    });
    
    // SECOND PRIORITY: Personalized based on feelings and goals
    // Stress/Anxiety → Meditations first, Grounding as a pause activity
    if (feelings.includes('stressed') || goals.includes('stress_relief')) {
      recommendations.push({
        name: "Guided Meditations",
        path: "/guided-meditations",
        icon: "🧘🏾",
        gradient: "bg-gradient-to-br from-cyan-500 to-blue-600",
        reason: "Cultivate peace and presence"
      });
    }
    
    // Sleep issues → Bedtime Routine & Sound Sanctuary
    if (goals.includes('better_sleep') || feelings.includes('overwhelmed')) {
      recommendations.push({
        name: "Sound Sanctuary",
        path: "/music",
        icon: "🎵",
        gradient: "bg-gradient-to-br from-pink-500 to-rose-600",
        reason: "Healing frequencies & sleep sounds"
      });
      recommendations.push({
        name: "Bedtime Routine",
        path: "/bedtime",
        icon: "🌙",
        gradient: "bg-gradient-to-br from-indigo-500 to-purple-600",
        reason: "Wind down for restorative rest"
      });
    }
    
    // Spiritual growth → Affirmations, Angel Numbers
    if (goals.includes('spiritual_growth') || spirituality === 'seeking_deeper' || spirituality === 'strong_practice') {
      recommendations.push({
        name: "Daily Affirmations",
        path: "/affirmations",
        icon: "✨",
        gradient: "bg-gradient-to-br from-amber-500 to-orange-600",
        reason: "Elevate your vibration daily"
      });
      recommendations.push({
        name: "Angel Numbers",
        path: "/angel-numbers",
        icon: "🔢",
        gradient: "bg-gradient-to-br from-violet-500 to-purple-600",
        reason: "Decode the universe's messages"
      });
    }
    
    // Health/Nutrition focus → Nutrition Hub
    if (focusArea === 'health' || goals.includes('nutrition') || goals.includes('weight_management') || goals.includes('energy')) {
      recommendations.push({
        name: "Nutrition & Wellness",
        path: "/nutrition",
        icon: "🥗",
        gradient: "bg-gradient-to-br from-green-500 to-lime-600",
        reason: "Nourish your temple mindfully"
      });
    }
    
    // Loneliness/Disconnected → Soul Circle
    if (feelings.includes('lonely') || feelings.includes('disconnected')) {
      recommendations.push({
        name: "Soul Circle",
        path: "/soul-circle",
        icon: "💜",
        gradient: "bg-gradient-to-br from-violet-500 to-pink-600",
        reason: "Connect with aligned souls"
      });
    }
    
    // Personal growth → Journal & Mindful Hobbies
    if (focusArea === 'personal' || goals.includes('mindfulness')) {
      recommendations.push({
        name: "Journal",
        path: "/journal",
        icon: "📝",
        gradient: "bg-gradient-to-br from-emerald-500 to-teal-600",
        reason: "Reflect, grow, and manifest"
      });
      recommendations.push({
        name: "Mindful Hobbies",
        path: "/mindful-hobbies",
        icon: "🎨",
        gradient: "bg-gradient-to-br from-rose-500 to-pink-600",
        reason: "Creative healing activities"
      });
    }
    
    // Creativity focus → Hobbies
    if (focusArea === 'creativity') {
      recommendations.push({
        name: "Mindful Hobbies",
        path: "/mindful-hobbies",
        icon: "🎨",
        gradient: "bg-gradient-to-br from-rose-500 to-pink-600",
        reason: "Express your creative spirit"
      });
    }
    
    // Relationship focus → Soul Circle & Healing Together
    if (focusArea === 'relationships') {
      recommendations.push({
        name: "Soul Circle",
        path: "/soul-circle",
        icon: "💜",
        gradient: "bg-gradient-to-br from-violet-500 to-pink-600",
        reason: "Build meaningful connections"
      });
    }
    
    // Career focus → Affirmations & Journal
    if (focusArea === 'career') {
      recommendations.push({
        name: "Daily Affirmations",
        path: "/affirmations",
        icon: "✨",
        gradient: "bg-gradient-to-br from-amber-500 to-orange-600",
        reason: "Manifest career success"
      });
      recommendations.push({
        name: "Journal",
        path: "/journal",
        icon: "📝",
        gradient: "bg-gradient-to-br from-emerald-500 to-teal-600",
        reason: "Plan and reflect on goals"
      });
    }
    
    // Hopeful/Peaceful → Keep the positive momentum
    if (feelings.includes('hopeful') || feelings.includes('peaceful')) {
      recommendations.push({
        name: "Morning Routine",
        path: "/morning",
        icon: "🌅",
        gradient: "bg-gradient-to-br from-amber-500 to-orange-600",
        reason: "Maintain your positive momentum"
      });
    }
    
    // Stuck → Vibe Check & Dream Journal
    if (feelings.includes('stuck')) {
      recommendations.push({
        name: "Daily Vibe Check",
        path: "/vibe-check",
        icon: "💫",
        gradient: "bg-gradient-to-br from-pink-500 to-rose-600",
        reason: "Understand your patterns"
      });
      recommendations.push({
        name: "Dream Journal",
        path: "/dreams",
        icon: "🌙",
        gradient: "bg-gradient-to-br from-indigo-500 to-purple-600",
        reason: "Unlock subconscious guidance"
      });
    }
    
    // Default recommendations if few matches
    const defaults = [
      { name: "Morning Routine", path: "/morning", icon: "🌅", gradient: "bg-gradient-to-br from-amber-500 to-orange-600", reason: "Start each day with intention" },
      { name: "Daily Vibe Check", path: "/vibe-check", icon: "💫", gradient: "bg-gradient-to-br from-pink-500 to-rose-600", reason: "Track your emotional wellness" },
      { name: "Readables", path: "/readables", icon: "📚", gradient: "bg-gradient-to-br from-violet-500 to-indigo-600", reason: "Expand your consciousness" },
      { name: "Dream Journal", path: "/dreams", icon: "🌙", gradient: "bg-gradient-to-br from-indigo-500 to-purple-600", reason: "Decode your subconscious" },
      { name: "Grounding Hour", path: "/grounding", icon: "🌳", gradient: "bg-gradient-to-br from-green-500 to-emerald-600", reason: "Find daily calm" }
    ];
    
    // Add defaults until we have 5 recommendations
    for (const def of defaults) {
      if (recommendations.length >= 5) break;
      if (!recommendations.some(r => r.path === def.path)) {
        recommendations.push(def);
      }
    }
    
    // Remove duplicates and limit to 5
    const unique = recommendations.filter((rec, index, self) => 
      index === self.findIndex(r => r.path === rec.path)
    );
    
    return unique.slice(0, 5);
  };

  // Generate daily flow schedule based on answers
  const generateDailyFlow = () => {
    const goals = answers.wellnessGoals || [];
    const feelings = answers.currentFeelings || [];
    const focusArea = answers.lifeAreaToImprove;
    
    const flow = [
      {
        time: "Morning",
        emoji: "🌅",
        color: "bg-amber-500",
        activity: "Rise & Align",
        description: "Start with morning affirmations and set your daily intention",
        path: "/morning",
        section: "Morning Routine"
      },
      {
        time: "Mid-Morning",
        emoji: "📝",
        color: "bg-emerald-500",
        activity: "Journal & Reflect",
        description: "Write your thoughts, track your dreams, or do a vibe check",
        path: "/journal",
        section: "Journal"
      }
    ];
    
    // Add based on goals
    if (goals.includes('nutrition') || focusArea === 'health' || goals.includes('weight_management')) {
      flow.push({
        time: "Midday",
        emoji: "🥗",
        color: "bg-green-500",
        activity: "Nourish & Move",
        description: "Log your meals and get in some mindful movement",
        path: "/nutrition",
        section: "Nutrition"
      });
    } else {
      flow.push({
        time: "Midday",
        emoji: "🧘🏾",
        color: "bg-cyan-500",
        activity: "Mindful Pause",
        description: "Take a breathing break or short meditation",
        path: "/guided-meditations",
        section: "Meditations"
      });
    }
    
    if (feelings.includes('stressed') || goals.includes('stress_relief') || feelings.includes('overwhelmed')) {
      flow.push({
        time: "Afternoon",
        emoji: "🌳",
        color: "bg-teal-500",
        activity: "Grounding Break",
        description: "Unplug for a few minutes and reconnect with yourself",
        path: "/grounding",
        section: "Grounding Hour"
      });
    } else if (focusArea === 'creativity' || feelings.includes('stuck')) {
      flow.push({
        time: "Afternoon",
        emoji: "🎨",
        color: "bg-rose-500",
        activity: "Creative Time",
        description: "Explore a mindful hobby or creative activity",
        path: "/mindful-hobbies",
        section: "Mindful Hobbies"
      });
    } else {
      flow.push({
        time: "Afternoon",
        emoji: "🎵",
        color: "bg-pink-500",
        activity: "Sound Break",
        description: "Listen to healing frequencies or uplifting music",
        path: "/music",
        section: "Sound Sanctuary"
      });
    }
    
    flow.push({
      time: "Evening",
      emoji: "🌙",
      color: "bg-indigo-500",
      activity: "Wind Down",
      description: "Prepare for rest with your bedtime ritual",
      path: "/bedtime",
      section: "Bedtime Routine"
    });
    
    return flow;
  };

  // Results Page
  if (showResults && quizResults) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 pb-24">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 pt-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-primary mb-2">Your Wellness Profile</h1>
            <p className="text-muted-foreground">Welcome to your personalized LaLa Land journey</p>
          </div>

          {/* Astrological Blueprint */}
          <Card className="mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Star className="h-5 w-5" />
                Your Astrological Blueprint
              </h2>
            </div>
            <CardContent className="p-6">
              {/* Main Grid - Sun, Moon, Rising, Life Path */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Sun Sign */}
                {quizResults.sunSign && (
                  <div className="text-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                    <span className="text-3xl mb-1 block">{quizResults.sunSign.symbol}</span>
                    <h3 className="font-bold text-primary text-sm">{quizResults.sunSign.sign}</h3>
                    <p className="text-xs text-amber-600 font-medium">Sun Sign</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{quizResults.sunSign.element} • Core Identity</p>
                  </div>
                )}
                
                {/* Moon Sign */}
                {quizResults.moonSign ? (
                  <div className="text-center p-4 bg-slate-100 dark:bg-slate-800/50 rounded-xl">
                    <span className="text-3xl mb-1 block">{quizResults.moonSign.symbol}</span>
                    <h3 className="font-bold text-primary text-sm">{quizResults.moonSign.sign}</h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">Moon Sign</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{quizResults.moonSign.element} • Emotions</p>
                  </div>
                ) : (
                  <div className="text-center p-4 bg-slate-100/50 dark:bg-slate-800/30 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600">
                    <Moon className="h-8 w-8 mx-auto mb-1 text-slate-400" />
                    <p className="text-xs text-muted-foreground font-medium">Moon Sign</p>
                    <p className="text-[10px] text-muted-foreground">Add birth time to calculate</p>
                  </div>
                )}
                
                {/* Rising Sign */}
                {quizResults.risingSign ? (
                  <div className="text-center p-4 bg-rose-50 dark:bg-rose-900/20 rounded-xl">
                    <span className="text-3xl mb-1 block">{quizResults.risingSign.symbol}</span>
                    <h3 className="font-bold text-primary text-sm">{quizResults.risingSign.sign}</h3>
                    <p className="text-xs text-rose-600 font-medium">Rising Sign</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{quizResults.risingSign.element} • First Impression</p>
                  </div>
                ) : (
                  <div className="text-center p-4 bg-rose-50/50 dark:bg-rose-900/10 rounded-xl border-2 border-dashed border-rose-300 dark:border-rose-800">
                    <Sun className="h-8 w-8 mx-auto mb-1 text-rose-400" />
                    <p className="text-xs text-muted-foreground font-medium">Rising Sign</p>
                    <p className="text-[10px] text-muted-foreground">Add birth time to calculate</p>
                  </div>
                )}
                
                {/* Life Path */}
                {quizResults.lifePath && (
                  <div className="text-center p-4 bg-violet-50 dark:bg-violet-900/20 rounded-xl">
                    <span className="text-3xl mb-1 block font-bold text-violet-600">{quizResults.lifePath.number}</span>
                    <h3 className="font-bold text-primary text-sm">{quizResults.lifePath.title}</h3>
                    <p className="text-xs text-violet-600 font-medium">Life Path</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Numerology • Purpose</p>
                  </div>
                )}
              </div>
              
              {quizResults.lifePath && (
                <p className="text-sm text-muted-foreground text-center italic">
                  "{quizResults.lifePath.description}"
                </p>
              )}
            </CardContent>
          </Card>

          {/* Spirit Guide & Angel Number */}
          <Card className="mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Compass className="h-5 w-5" />
                Your Spiritual Guides
              </h2>
            </div>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Animal Guide */}
                <div className="text-center p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
                  <span className="text-4xl mb-2 block">{quizResults.animalGuide.emoji || '🦋'}</span>
                  <h3 className="font-bold text-primary">{quizResults.animalGuide.name}</h3>
                  <p className="text-xs text-muted-foreground mb-2">Spirit Animal</p>
                  <p className="text-xs text-muted-foreground">{quizResults.animalGuide.description}</p>
                </div>
                
                {/* Angel Number */}
                <div className="text-center p-4 bg-rose-50 dark:bg-rose-900/20 rounded-xl">
                  <span className="text-2xl font-bold text-rose-600 mb-2 block">{quizResults.angelNumber.number}</span>
                  <h3 className="font-bold text-primary">{quizResults.angelNumber.meaning}</h3>
                  <p className="text-xs text-muted-foreground mb-2">Lucky Angel Number</p>
                  <p className="text-xs text-muted-foreground">{quizResults.angelNumber.message}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Book Recommendations */}
          <Card className="mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Heart className="h-5 w-5" />
                Recommended Reads for You
              </h2>
            </div>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground mb-4">Based on your goals and current journey:</p>
              <div className="space-y-3">
                {quizResults.bookRecommendations.map((book, index) => (
                  <div 
                    key={book.id}
                    className="flex items-center gap-3 p-3 bg-background rounded-lg border hover:border-amber-500/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/readables?book=${book.id}`)}
                  >
                    <span className="text-lg font-bold text-amber-600">{index + 1}</span>
                    <div className="flex-1">
                      <h4 className="font-medium text-primary text-sm">{book.title}</h4>
                      <p className="text-xs text-muted-foreground">{book.reason}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Wellness Snapshot */}
          <Card className="mb-6 overflow-hidden border-2 border-violet-500/30">
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 p-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                Your Wellness Snapshot
              </h2>
              <p className="text-violet-100 text-sm">This will be displayed on your profile</p>
            </div>
            <CardContent className="p-6">
              {/* Astrological Row */}
              <div className="grid grid-cols-4 gap-3 text-center mb-4">
                <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <p className="text-xl">{quizResults.sunSign?.symbol || "?"}</p>
                  <p className="text-[10px] text-muted-foreground">Sun</p>
                </div>
                <div className="p-2 bg-slate-100 dark:bg-slate-800/50 rounded-lg">
                  <p className="text-xl">{quizResults.moonSign?.symbol || "?"}</p>
                  <p className="text-[10px] text-muted-foreground">Moon</p>
                </div>
                <div className="p-2 bg-rose-50 dark:bg-rose-900/20 rounded-lg">
                  <p className="text-xl">{quizResults.risingSign?.symbol || "?"}</p>
                  <p className="text-[10px] text-muted-foreground">Rising</p>
                </div>
                <div className="p-2 bg-violet-50 dark:bg-violet-900/20 rounded-lg">
                  <p className="text-xl font-bold text-violet-600">{quizResults.lifePath?.number || "?"}</p>
                  <p className="text-[10px] text-muted-foreground">Life Path</p>
                </div>
              </div>
              
              {/* Spiritual Row */}
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                  <p className="text-xl mb-0.5">{quizResults.animalGuide.emoji || '🦋'}</p>
                  <p className="text-sm font-medium text-emerald-600">{quizResults.animalGuide.name}</p>
                  <p className="text-[10px] text-muted-foreground">Spirit Guide</p>
                </div>
                <div className="p-2 bg-rose-50 dark:bg-rose-900/20 rounded-lg">
                  <p className="text-sm font-bold text-rose-600">{quizResults.angelNumber.number}</p>
                  <p className="text-[10px] text-muted-foreground">Angel Number</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upsell - Only show if missing Moon/Rising */}
          {(!quizResults.moonSign || !quizResults.risingSign) && (
            <Card className="mb-6 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>Tip:</strong> Add your birth time in the quiz to get your Moon & Rising signs!
                </p>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowResults(false);
                    setCurrentStep(0);
                  }}
                >
                  Go Back & Add Birth Time
                </Button>
              </CardContent>
            </Card>
          )}

          {/* YOUR JOURNEY STARTS HERE - Personalized Recommendations */}
          <Card className="mb-6 overflow-hidden border-2 border-emerald-500/30">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Target className="h-5 w-5" />
                Your Journey Starts Here
              </h2>
              <p className="text-emerald-100 text-sm">Personalized recommendations based on your answers</p>
            </div>
            <CardContent className="p-6">
              <div className="space-y-3">
                {generateJourneyRecommendations().map((rec, index) => (
                  <button
                    key={rec.path}
                    onClick={() => navigate(rec.path)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                      index === 0 
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' 
                        : 'border-border hover:border-emerald-500/50'
                    }`}
                    data-testid={`journey-rec-${rec.path.replace('/', '')}`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${rec.gradient}`}>
                      <span className="text-xl">{rec.icon}</span>
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-primary">{rec.name}</h3>
                        {index === 0 && (
                          <span className="px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full">
                            START HERE
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{rec.reason}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* YOUR DAILY FLOW - Suggested Schedule */}
          <Card className="mb-6 overflow-hidden border-2 border-violet-500/30">
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 p-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Your Daily Flow
              </h2>
              <p className="text-violet-100 text-sm">A suggested rhythm for your day</p>
            </div>
            <CardContent className="p-6">
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-4 bottom-4 w-0.5 bg-gradient-to-b from-amber-500 via-emerald-500 to-indigo-500"></div>
                
                <div className="space-y-4">
                  {generateDailyFlow().map((item, index) => (
                    <div key={item.time} className="flex items-start gap-4 pl-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white z-10 ${item.color}`}>
                        <span className="text-sm">{item.emoji}</span>
                      </div>
                      <div className="flex-1 pb-2">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-muted-foreground">{item.time}</span>
                          <span className="text-sm font-semibold text-primary">{item.activity}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                        {item.path && (
                          <button 
                            onClick={() => navigate(item.path)}
                            className="mt-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
                          >
                            Go to {item.section} <ChevronRight className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-violet-500/10 rounded-lg border border-violet-500/20">
                <p className="text-xs text-center text-violet-600 dark:text-violet-400">
                  This flow adapts to your goals. Revisit your profile anytime to update preferences.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="space-y-3">
            {/* Primary CTA - Go to recommended section */}
            <Button 
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-6 text-lg"
              onClick={() => navigate(generateJourneyRecommendations()[0]?.path || "/dashboard")}
              data-testid="start-journey-btn"
            >
              Start with {generateJourneyRecommendations()[0]?.name || "Your Journey"}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            {/* Secondary CTA - Dashboard */}
            <Button 
              variant="outline"
              className="w-full py-4"
              onClick={() => navigate("/dashboard")}
              data-testid="go-dashboard-btn"
            >
              Explore Dashboard First
            </Button>
          </div>
          
          {/* Revisit Note */}
          <p className="text-center text-xs text-muted-foreground mt-4">
            You can retake this quiz anytime from your Profile settings.
          </p>
        </div>
      </div>
    );
  }

  // Quiz Steps
  const renderStep = () => {
    switch (currentStep) {
      // Step 0: Gender/Identity Selection (NEW)
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Heart className="h-12 w-12 mx-auto mb-3 text-pink-500" />
              <h2 className="text-2xl font-bold text-primary">Welcome, Beautiful Soul</h2>
              <p className="text-muted-foreground">Help us personalize your wellness journey</p>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm text-center text-muted-foreground mb-4">
                This helps us tailor health tracking, affirmations, and wellness content specifically for you.
              </p>
              
              <div className="grid grid-cols-1 gap-3">
                {[
                  { id: "female", label: "Woman / Queen", emoji: "👑", description: "Menstrual cycle tracking & women's wellness" },
                  { id: "male", label: "Man / King", emoji: "🦁", description: "Men's health tracking & masculine energy" },
                  { id: "non_binary", label: "Non-Binary / Fluid", emoji: "🌈", description: "Universal wellness tracking" },
                  { id: "prefer_not_say", label: "Prefer Not to Say", emoji: "✨", description: "General wellness features" }
                ].map(option => (
                  <button
                    key={option.id}
                    onClick={() => setAnswers({...answers, gender: option.id})}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      answers.gender === option.id
                        ? "border-emerald-500 bg-emerald-500/10"
                        : "border-border/50 hover:border-emerald-500/50 bg-card"
                    }`}
                    data-testid={`gender-${option.id}-btn`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{option.emoji}</span>
                      <div>
                        <p className="font-semibold text-primary">{option.label}</p>
                        <p className="text-xs text-muted-foreground">{option.description}</p>
                      </div>
                      {answers.gender === option.id && (
                        <CheckCircle className="h-5 w-5 text-emerald-500 ml-auto" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
              
              <div className="mt-4 p-3 bg-violet-500/10 rounded-lg border border-violet-500/20">
                <p className="text-xs text-center text-violet-600 dark:text-violet-400">
                  🙏🏾 Lala Land is a healing space for the entire community — men, women, and everyone in between.
                </p>
              </div>
            </div>
          </div>
        );
      
      // Step 1: Birth Information (was Step 0)
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Sun className="h-12 w-12 mx-auto mb-3 text-amber-500" />
              <h2 className="text-2xl font-bold text-primary">Astrological Foundations</h2>
              <p className="text-muted-foreground">Let's discover your cosmic blueprint</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Date of Birth *
                </label>
                <Input
                  type="date"
                  value={answers.birthDate}
                  onChange={(e) => setAnswers({...answers, birthDate: e.target.value})}
                  className="w-full"
                  data-testid="birth-date-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Required to calculate your Sun Sign & Life Path</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  <Clock className="h-4 w-4 inline mr-2" />
                  Time of Birth (Optional)
                </label>
                <Input
                  type="time"
                  value={answers.birthTime}
                  onChange={(e) => setAnswers({...answers, birthTime: e.target.value})}
                  className="w-full"
                  data-testid="birth-time-input"
                />
                <div className="mt-2 p-2 bg-violet-500/10 rounded-lg border border-violet-500/20">
                  <p className="text-xs text-violet-600 dark:text-violet-400 font-medium">
                    💡 You can find this on your birth certificate!
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Birth time unlocks your Moon & Rising signs for a complete cosmic blueprint.
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  <MapPin className="h-4 w-4 inline mr-2" />
                  Place of Birth (Optional)
                </label>
                <Input
                  type="text"
                  placeholder="City, Country"
                  value={answers.birthPlace}
                  onChange={(e) => setAnswers({...answers, birthPlace: e.target.value})}
                  className="w-full"
                  data-testid="birth-place-input"
                />
              </div>
            </div>
          </div>
        );

      // Step 2: Wellness Goals (was Step 1)
      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Target className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
              <h2 className="text-2xl font-bold text-primary">Wellness Intentions</h2>
              <p className="text-muted-foreground">Choose up to 3 primary goals</p>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {[
                { id: "stress", label: "Managing stress and anxiety", icon: "🧘🏾" },
                { id: "sleep", label: "Improving sleep quality", icon: "😴" },
                { id: "spiritual", label: "Enhancing spiritual connection", icon: "✨" },
                { id: "recipes", label: "Discovering new recipes and meal ideas", icon: "🥗" },
                { id: "energy", label: "Increasing energy levels", icon: "⚡" },
                { id: "mindfulness", label: "Cultivating mindfulness practice", icon: "🧠" },
                { id: "weight", label: "Weight management", icon: "⚖️" },
                { id: "nutrition", label: "Learning more about nutrition", icon: "🍎" }
              ].map((goal) => (
                <button
                  key={goal.id}
                  onClick={() => handleMultiSelect("wellnessGoals", goal.id, 3)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    answers.wellnessGoals.includes(goal.id)
                      ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20"
                      : "border-border hover:border-emerald-500/50"
                  }`}
                  data-testid={`goal-${goal.id}`}
                >
                  <span className="text-xl mr-3">{goal.icon}</span>
                  <span className="font-medium">{goal.label}</span>
                  {answers.wellnessGoals.includes(goal.id) && (
                    <CheckCircle className="h-5 w-5 float-right text-emerald-500" />
                  )}
                </button>
              ))}
            </div>
            
            <p className="text-center text-sm text-muted-foreground">
              Selected: {answers.wellnessGoals.length}/3
            </p>
          </div>
        );

      // Step 3: Dietary Preferences (was Step 2)
      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Utensils className="h-12 w-12 mx-auto mb-3 text-orange-500" />
              <h2 className="text-2xl font-bold text-primary">Nutritional Preferences</h2>
              <p className="text-muted-foreground">Help us personalize your meal recommendations</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-primary mb-3">Dietary Preference</label>
              <div className="grid grid-cols-1 gap-3">
                {[
                  { id: "omnivore", label: "Omnivore", desc: "Balanced diet including meat, vegetables, grains, dairy" },
                  { id: "pescatarian", label: "Pescatarian", desc: "Fish and seafood, but no other meat" },
                  { id: "vegetarian", label: "Vegetarian", desc: "No meat, poultry, or fish" },
                  { id: "vegan", label: "Vegan", desc: "No animal products" },
                  { id: "none", label: "No preference", desc: "I eat everything" }
                ].map((diet) => (
                  <button
                    key={diet.id}
                    onClick={() => setAnswers({...answers, dietaryPreference: diet.id})}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      answers.dietaryPreference === diet.id
                        ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                        : "border-border hover:border-orange-500/50"
                    }`}
                    data-testid={`diet-${diet.id}`}
                  >
                    <p className="font-medium">{diet.label}</p>
                    <p className="text-xs text-muted-foreground">{diet.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-primary mb-3">Food Allergies (Select all that apply)</label>
              <div className="flex flex-wrap gap-2">
                {["Gluten", "Dairy", "Nuts", "Soy", "Shellfish", "Eggs", "None"].map((allergy) => (
                  <button
                    key={allergy}
                    onClick={() => handleMultiSelect("foodAllergies", allergy.toLowerCase())}
                    className={`px-4 py-2 rounded-full border transition-all ${
                      answers.foodAllergies.includes(allergy.toLowerCase())
                        ? "border-orange-500 bg-orange-500 text-white"
                        : "border-border hover:border-orange-500/50"
                    }`}
                    data-testid={`allergy-${allergy.toLowerCase()}`}
                  >
                    {allergy}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      // Step 4: Spirituality (was Step 3)
      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Moon className="h-12 w-12 mx-auto mb-3 text-violet-500" />
              <h2 className="text-2xl font-bold text-primary">Understanding Your Spirituality</h2>
              <p className="text-muted-foreground">Share your spiritual journey</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-primary mb-3">
                How would you describe your current relationship with spirituality?
              </label>
              <div className="grid grid-cols-1 gap-3">
                {[
                  { id: "exploring", label: "Just starting to explore", icon: "🌱" },
                  { id: "developing", label: "I have a developing practice", icon: "🌿" },
                  { id: "strong", label: "I have a strong, consistent practice", icon: "🌳" },
                  { id: "not_interested", label: "Not interested in spiritual practices", icon: "🤔" }
                ].map((level) => (
                  <button
                    key={level.id}
                    onClick={() => setAnswers({...answers, spiritualRelationship: level.id})}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      answers.spiritualRelationship === level.id
                        ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20"
                        : "border-border hover:border-violet-500/50"
                    }`}
                    data-testid={`spiritual-${level.id}`}
                  >
                    <span className="text-xl mr-3">{level.icon}</span>
                    <span className="font-medium">{level.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-primary mb-2">
                What brings you a sense of peace and connection? (Optional)
              </label>
              <Textarea
                placeholder="Nature, meditation, music, art, community..."
                value={answers.peaceBrings}
                onChange={(e) => setAnswers({...answers, peaceBrings: e.target.value})}
                className="w-full"
                data-testid="peace-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-primary mb-3">
                How important is intuition in your decision-making?
              </label>
              <div className="flex flex-wrap gap-2">
                {["Not at all", "Slightly", "Moderately", "Very", "Extremely"].map((level, i) => (
                  <button
                    key={level}
                    onClick={() => setAnswers({...answers, intuitionImportance: level.toLowerCase()})}
                    className={`px-4 py-2 rounded-full border transition-all ${
                      answers.intuitionImportance === level.toLowerCase()
                        ? "border-violet-500 bg-violet-500 text-white"
                        : "border-border hover:border-violet-500/50"
                    }`}
                    data-testid={`intuition-${level.toLowerCase()}`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      // Step 5: Animal Spirit & Angel Numbers (was Step 4)
      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Compass className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
              <h2 className="text-2xl font-bold text-primary">Spiritual Guides</h2>
              <p className="text-muted-foreground">Discover your animal spirit and angel numbers</p>
            </div>
            
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
              <label className="block text-sm font-medium text-primary mb-3">
                Do you feel a strong connection to a particular animal?
              </label>
              <div className="flex gap-3 mb-4">
                {["Yes", "No", "Unsure"].map((option) => (
                  <button
                    key={option}
                    onClick={() => setAnswers({...answers, hasAnimalConnection: option.toLowerCase()})}
                    className={`flex-1 py-2 rounded-lg border transition-all ${
                      answers.hasAnimalConnection === option.toLowerCase()
                        ? "border-emerald-500 bg-emerald-500 text-white"
                        : "border-border hover:border-emerald-500/50"
                    }`}
                    data-testid={`animal-${option.toLowerCase()}`}
                  >
                    {option}
                  </button>
                ))}
              </div>
              
              {answers.hasAnimalConnection === "yes" && (
                <div>
                  <label className="block text-xs text-muted-foreground mb-2">
                    Describe this connection or encounter
                    <span className="text-muted-foreground/70 ml-1">(optional)</span>
                  </label>
                  <Textarea
                    placeholder="I've always felt drawn to wolves because..."
                    value={answers.animalDescription}
                    onChange={(e) => setAnswers({...answers, animalDescription: e.target.value})}
                    className="w-full"
                    data-testid="animal-description"
                  />
                  <p className="mt-1 text-[11px] text-muted-foreground/70 italic">
                    Skip this if you'd rather. We just love hearing your story 🌿
                  </p>
                </div>
              )}
            </div>
            
            <div className="p-4 bg-rose-50 dark:bg-rose-900/20 rounded-xl">
              <label className="block text-sm font-medium text-primary mb-3">
                Do you notice repeating number sequences (111, 222, 333)?
              </label>
              <div className="flex gap-3 mb-4">
                {["Yes", "No", "Unsure"].map((option) => (
                  <button
                    key={option}
                    onClick={() => setAnswers({...answers, noticesAngelNumbers: option.toLowerCase()})}
                    className={`flex-1 py-2 rounded-lg border transition-all ${
                      answers.noticesAngelNumbers === option.toLowerCase()
                        ? "border-rose-500 bg-rose-500 text-white"
                        : "border-border hover:border-rose-500/50"
                    }`}
                    data-testid={`numbers-${option.toLowerCase()}`}
                  >
                    {option}
                  </button>
                ))}
              </div>
              
              {answers.noticesAngelNumbers === "yes" && (
                <div>
                  <label className="block text-xs text-muted-foreground mb-2">
                    Which numbers do you see most often?
                    <span className="text-muted-foreground/70 ml-1">(optional)</span>
                  </label>
                  <Input
                    placeholder="e.g., 11:11, 444, 777..."
                    value={answers.frequentNumbers}
                    onChange={(e) => setAnswers({...answers, frequentNumbers: e.target.value})}
                    className="w-full"
                    data-testid="frequent-numbers"
                  />
                </div>
              )}
            </div>
          </div>
        );

      // Step 6: Current State (was Step 5)
      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Brain className="h-12 w-12 mx-auto mb-3 text-blue-500" />
              <h2 className="text-2xl font-bold text-primary">Your Inner Landscape</h2>
              <p className="text-muted-foreground">Help us understand where you are right now</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-primary mb-3">
                How are you feeling right now? (Select all that apply)
              </label>
              <div className="grid grid-cols-1 gap-2">
                {[
                  { id: "stressed", label: "Stressed and anxious" },
                  { id: "stuck", label: "Unmotivated and stuck" },
                  { id: "overwhelmed", label: "Overwhelmed by responsibilities" },
                  { id: "doubtful", label: "Doubtful of my abilities" },
                  { id: "lonely", label: "Lonely or disconnected" },
                  { id: "frustrated", label: "Frustrated with my progress" },
                  { id: "positive", label: "Generally positive and content" }
                ].map((feeling) => (
                  <button
                    key={feeling.id}
                    onClick={() => handleMultiSelect("currentFeelings", feeling.id)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      answers.currentFeelings.includes(feeling.id)
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-border hover:border-blue-500/50"
                    }`}
                    data-testid={`feeling-${feeling.id}`}
                  >
                    {answers.currentFeelings.includes(feeling.id) && (
                      <CheckCircle className="h-4 w-4 inline mr-2 text-blue-500" />
                    )}
                    {feeling.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-primary mb-3">
                What area of your life would you most like to improve?
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: "career", label: "Career", icon: "💼" },
                  { id: "relationships", label: "Relationships", icon: "💕" },
                  { id: "finances", label: "Finances", icon: "💰" },
                  { id: "health", label: "Health & Wellness", icon: "🏃🏾" },
                  { id: "personal", label: "Personal Growth", icon: "🌱" },
                  { id: "creativity", label: "Creativity/Hobbies", icon: "🎨" }
                ].map((area) => (
                  <button
                    key={area.id}
                    onClick={() => setAnswers({...answers, lifeAreaToImprove: area.id})}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      answers.lifeAreaToImprove === area.id
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-border hover:border-blue-500/50"
                    }`}
                    data-testid={`area-${area.id}`}
                  >
                    <span className="text-2xl block mb-1">{area.icon}</span>
                    <span className="text-sm font-medium">{area.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 pb-24">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pt-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-emerald-500" />
            <span className="font-semibold text-primary">LaLa Land</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            className="text-muted-foreground hover:text-primary"
            data-testid="skip-quiz-btn"
          >
            <SkipForward className="h-4 w-4 mr-1" />
            Skip for now
          </Button>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span className="font-medium">Step {currentStep + 1} of {totalSteps}</span>
            <span className="tabular-nums">{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2 transition-all duration-500 ease-out" />
          {/* Step dots for at-a-glance context */}
          <div className="flex justify-between mt-3 px-1">
            {Array.from({ length: totalSteps }).map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 flex-1 mx-0.5 rounded-full transition-all duration-500 ${
                  idx < currentStep
                    ? "bg-emerald-500"
                    : idx === currentStep
                    ? "bg-gradient-to-r from-emerald-500 to-teal-500 shadow-sm animate-pulse"
                    : "bg-muted"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Quiz Content */}
        <Card className="mb-6 overflow-hidden">
          <CardContent className="p-6">
            <div key={currentStep} className="animate-in fade-in slide-in-from-right-4 duration-500">
              {renderStep()}
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex gap-3">
          {currentStep > 0 && (
            <Button
              variant="outline"
              onClick={() => setCurrentStep(currentStep - 1)}
              className="flex-1"
              data-testid="prev-step-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
          
          {currentStep < totalSteps - 1 ? (
            <Button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceed()}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
              data-testid="next-step-btn"
            >
              Continue
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmitQuiz}
              disabled={!canProceed() || isSubmitting}
              className="flex-1 bg-gradient-to-r from-violet-500 to-purple-500 text-white"
              data-testid="submit-quiz-btn"
            >
              {isSubmitting ? "Creating Your Profile..." : "See My Results"}
              <Sparkles className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Intro Text */}
        {currentStep === 0 && (
          <p className="text-center text-sm text-muted-foreground mt-6">
            This quiz will help us tailor your LaLa Land experience with personalized 
            insights and recommendations based on your unique profile.
          </p>
        )}
      </div>
    </div>
  );
};

export default OnboardingQuiz;
